import axios from 'axios';
import { action, computed, makeObservable } from 'mobx';

import { AlertsStore } from '@/configs/store/AlertsStore';
import { IS_V2_WRITE_ENABLED } from '@/configs/v2Write';
import { LoadingStageModel } from '@/models/LoadingStageModel';
import { LocalStore } from '@/models/LocalStore';
import { ValueModel } from '@/models/ValueModel';
import { trackerApi } from '@/tracker/api/trackerApi';
import { TrackerStore } from '@/tracker/model';
import { Board } from '@/tracker/types';
import { showError } from '@/tracker/utils/showError';

/** Общее у вкладок «Бэклог» и «Доска»: сама доска и поиск. Шапка с ними не перерисовывается при смене вкладки */
export class BoardLayoutStore extends LocalStore {
  readonly boardId: string;
  private readonly _alertsStore: AlertsStore;
  private readonly _trackerStore: TrackerStore;
  private readonly _userId: string | null;
  private readonly _isAdmin: boolean;
  private readonly _board = new ValueModel<Board | null>(null);
  /** Поиск по ключу и названию среди загруженных задач вкладки */
  private readonly _query = new ValueModel('');
  private readonly _isNotFound = new ValueModel(false);
  private readonly _loadingStage = new LoadingStageModel();

  constructor({
    alertsStore,
    trackerStore,
    boardId,
    userId,
    isAdmin,
  }: {
    alertsStore: AlertsStore;
    trackerStore: TrackerStore;
    boardId: string;
    userId: string | null;
    isAdmin: boolean;
  }) {
    super();
    this.boardId = boardId;
    this._alertsStore = alertsStore;
    this._trackerStore = trackerStore;
    this._userId = userId;
    this._isAdmin = isAdmin;

    makeObservable(this, {
      board: computed,
      query: computed,
      canManage: computed,
      isNotFound: computed,
      isError: computed,
      load: action.bound,
      setQuery: action.bound,
      setBoard: action.bound,
    });
  }

  get board() {
    return this._board.value;
  }

  get query() {
    return this._query.value;
  }

  /** Доской и её спринтами управляют владелец и админ — остальным бэкенд ответит 403 not_board_owner */
  get canManage() {
    // Трекер пока бэкап Яндекс Трекера: доски только смотрим
    if (!IS_V2_WRITE_ENABLED) return false;

    return this._isAdmin || (!!this._userId && this._board.value?.owner?.id === this._userId);
  }

  get isNotFound() {
    return this._isNotFound.value;
  }

  get isError() {
    return this._loadingStage.isError;
  }

  setQuery(query: string) {
    this._query.change(query);
  }

  /** Доску изменили в настройках или вкладка перечитала её после действия со спринтом */
  setBoard(board: Board) {
    this._board.change(board);
    this._trackerStore.addRecent({ type: 'board', id: board.id, name: board.name });
  }

  async load() {
    try {
      this._loadingStage.loading();
      this.setBoard(await trackerApi.getBoard(this.boardId));
      this._loadingStage.success();
    } catch (error) {
      this._loadingStage.error();

      if (axios.isAxiosError(error) && error.response?.status === 404) {
        this._isNotFound.change(true);

        return;
      }

      showError({ alertsStore: this._alertsStore, title: 'Не удалось загрузить доску', error });
    }
  }
}
